import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import DocumentModal from './DocumentModal';
import { getErrorMessages, isImage } from '../../utils';
import { TDVLACheckSession, TDVLACheckSessions } from '../../types';
import { ModalConfirmationWithErrorAndLoader } from '../common/ModalConfirmationWithErrorAndLoader';
import { executeHttpRequest } from '../../epics/api';


type TFreelancerDetailsDVLAChecksProps = {
    freelancerId: number;
    fetchListOf: (a: string, b: string) => Promise<any>;
    dvlaCheckSessions: TDVLACheckSessions;
};

export default function FreelancerDetailsDVLACheckSessions({
    freelancerId,
    fetchListOf,
    dvlaCheckSessions,
}: TFreelancerDetailsDVLAChecksProps) {
    const [popupCheck, setPopupCheck] = useState(null);
    const [isModalAddCheckOpen, setIsModalAddCheckOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [intervalId, setIntervalId] = useState(null);
    const intervalIdRef = useRef();

    const startInterval = () => setIntervalId(
        setInterval(fetchListOf, 10000, 'dvlaCheckSessions', `freelancers/${freelancerId}/dvla-check-sessions/`)
    );

    const stopInterval = () => {
        clearInterval(intervalIdRef.current);
        setIntervalId(null);
    };

    // Start or stop interval based on the checks status
    useEffect(() => {
        const shouldRefresh = dvlaCheckSessions.content?.some(
            (session: TDVLACheckSession) => ['Waiting', 'Processing', 'Retrying'].includes(session.status));

        if (shouldRefresh && !intervalId) {
            startInterval();
        } else if (!shouldRefresh && intervalId) {
            stopInterval();
            fetchListOf('documents', `documents/?freelancer=${freelancerId}`);
        }
    }, [dvlaCheckSessions]);

    // Save intervalId to ref to use it in the cleanup function
    useEffect(() => {
        intervalIdRef.current = intervalId;
    }, [intervalId]);

    // Cleanup interval on unmount
    useEffect(() => () => stopInterval(), []);

    const openPopup = (session: TDVLACheckSession) => {
        setPopupCheck(session);
    };

    const closePopup = () => {
        setPopupCheck(null);
    };

    const handleOpenAddCheck = () => {
        setError(null);
        setIsModalAddCheckOpen(true);
    };

    const handleCancelAddCheck = () => {
        setError(null);
        setIsModalAddCheckOpen(false);
    };

    const handleAddCheck = () => {
        setIsLoading(true);
        setError(null);

        executeHttpRequest(`freelancers/${freelancerId}/dvla-check-sessions/`, 'post', {})
            .then(() => {
                fetchListOf('dvlaCheckSessions', `freelancers/${freelancerId}/dvla-check-sessions/`)
                    .then(() => {
                        setIsLoading(false);
                        handleCancelAddCheck();
                    });

            })
            .catch(err => {
                setIsLoading(false);
                setError(getErrorMessages(err));
            });
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Success':
                return 'green';
            case 'Error':
            case 'Not found':
                return 'red';
            default:
                return 'orange';
        }
    };

    const renderLink = (session: TDVLACheckSession) => {
        if (isImage(session.file)) {
            return <a onClick={() => openPopup(session)}>DVLA Check</a>;
        }
        return <a href={session.file} target="_blank" rel="noreferrer" download={true}>DVLA Check</a>;
    };

    const renderRows = () =>
        dvlaCheckSessions.content.map((session: TDVLACheckSession) =>
            <tr key={session.id}>
                <td>
                    {moment(session.created).format('D MMM YYYY, HH:mm')}
                </td>
                <td>
                    {moment(session.updated).format('D MMM YYYY, HH:mm')}
                </td>
                <td>
                    <span style={{ color: getStatusColor(session.status) }}>{session.status}</span>
                </td>
                <td>
                    {session.file ? renderLink(session) : session.detail}
                </td>
            </tr>
        );

    return (
        <div className="freelancer-details-section">
            <div className="freelancer-details-section__header">
                <div className="freelancer-details-section__title">
                    DVLA Check Requests
                </div>
                <div className="freelancer-details-section__btns display-block">
                    <button
                        className="freelancer-details-section__btn"
                        onClick={handleOpenAddCheck}
                        type="button"
                    >
                        <i className="icon with-text icon-add-12" />
                        Add
                    </button>
                    {!!dvlaCheckSessions.loading && (
                        <i className="icon icon-loading-1 font-size-30" />
                    )}
                </div>
            </div>
            {error && !isModalAddCheckOpen && (
                <div className="freelancer-details-section__errors">
                    <p>{error}</p>
                </div>
            )}
            <div className="freelancer-details-section__content">
                {dvlaCheckSessions.content.length === 0 ? (
                    <div>Worker doesn&apos;t have any check requests yet.</div>
                ) : (
                    <div>
                        <table className="table-default">
                            <tbody>
                                <tr>
                                    <td className="table-default__cell--text-blue table-default__cell--date">
                                        Created
                                    </td>
                                    <td className="table-default__cell--text-blue" style={{ width: '220px' }}>
                                        Updated
                                    </td>
                                    <td className="table-default__cell--text-blue" style={{ width: '220px' }}>
                                        Status
                                    </td>
                                    <td className="table-default__cell--text-blue">
                                        Detail
                                    </td>
                                </tr>
                                {renderRows()}
                            </tbody>
                        </table>
                        {popupCheck && (
                            <DocumentModal
                                isOpen={true}
                                src={popupCheck.file}
                                onClose={closePopup}
                                title="DVLA Consent"
                            />
                        )}
                    </div>
                )}
                {isModalAddCheckOpen && (
                    <ModalConfirmationWithErrorAndLoader
                        className={null}
                        errorMessage={error}
                        handleCancel={handleCancelAddCheck}
                        handleContinue={handleAddCheck}
                        isError={!!error}
                        isInProgress={isLoading}
                        setIsInProgress={setIsLoading}
                        isOpen={isModalAddCheckOpen}
                        title="Add DVLA Check"
                    >
                        <p className="modal-title">DVLA Check</p>
                        <p>Are you sure that you would like to proceed with a DVLA check for this worker?</p><br/>
                    </ModalConfirmationWithErrorAndLoader>
                )}
            </div>
        </div>
    );
}
